import { createStore } from 'vuex';
import createPersistedState from 'vuex-persistedstate';

const loadInitialCart = () => {
    try {
        const stored = sessionStorage.getItem('avem_cart');
        return stored ? JSON.parse(stored) : [];
    } catch (e) {
        console.error("Failed to parse cart from sessionStorage:", e);
        return [];
    }
};

const saveCartToSession = (cart) => {
    try {
        sessionStorage.setItem('avem_cart', JSON.stringify(cart));
    } catch (e) {
        console.error("Failed to save cart to sessionStorage:", e);
    }
};

const loadInitialFavorites = () => {
    try {
        const stored = sessionStorage.getItem('avem_favorites');
        return stored ? JSON.parse(stored) : {};
    } catch (e) {
        console.error("Failed to parse favorites from sessionStorage:", e);
        return {};
    }
};

const saveFavoritesToSession = (favorites) => {
    try {
        sessionStorage.setItem('avem_favorites', JSON.stringify(favorites));
    } catch (e) {
        console.error("Failed to save favorites to sessionStorage:", e);
    }
};

let fetchProductsPromise = null;

export default createStore({
    plugins: [createPersistedState()],
    state: {
        terms: JSON.parse(localStorage.getItem('terms') || "{}"),
        languageCode: localStorage.getItem('languageCode') || 'bg',
        validationRules: {},
        env: "development",
        cookieConsent: false,
        cart: loadInitialCart(),
        favorites: loadInitialFavorites(),
        calculatedDeliveryCost: null,
        products: [],
        productsLanguage: null,
        productsLoading: false
    },
    mutations: {
        setCalculatedDeliveryCost(state, cost) {
            state.calculatedDeliveryCost = cost;
        },
        setEnv(state, env) {
            console.log("Setting environment to:", env);
            state.env = env;
        },
        setTerms(state, terms) {
            state.terms = terms;
            localStorage.setItem('terms', JSON.stringify(terms)); // Store as a JSON string
        },
        setLanguageCode(state, code) {
            state.languageCode = code;
            localStorage.setItem('languageCode', code);
        },
        setValidationRules(state, rules) {
            state.validationRules = rules;
        },
        setCookieConsent(state, cookieConsent) {
            state.cookieConsent = cookieConsent;
        },
        addToCart(state, { product, quantity = 1 }) {
            const qty = Math.max(1, parseInt(quantity) || 1);
            const existing = state.cart.find(item => item.id === product.id);
            if (existing) {
                existing.quantity = qty;
            } else {
                state.cart.push({
                    id: product.id,
                    product: { ...product },
                    quantity: qty
                });
            }
            saveCartToSession(state.cart);
        },
        updateCartQuantity(state, { productId, quantity }) {
            const index = state.cart.findIndex(item => item.id === productId);
            if (index !== -1) {
                const qty = parseInt(quantity) || 0;
                if (qty <= 0) {
                    state.cart.splice(index, 1);
                } else {
                    state.cart[index].quantity = qty;
                }
                saveCartToSession(state.cart);
            }
        },
        removeFromCart(state, productId) {
            state.cart = state.cart.filter(item => item.id !== productId);
            saveCartToSession(state.cart);
        },
        clearCart(state) {
            state.cart = [];
            sessionStorage.removeItem('avem_cart');
        },
        toggleFavorite(state, productId) {
            const isFav = !state.favorites[productId];
            state.favorites = {
                ...state.favorites,
                [productId]: isFav ? Date.now() : false
            };
            saveFavoritesToSession(state.favorites);
        },
        setProducts(state, { products, langCode }) {
            state.products = products;
            state.productsLanguage = langCode;
        },
        setProductsLoading(state, loading) {
            state.productsLoading = loading;
        }
    },
    actions: {
        async fetchProducts({ commit, state }, { axios, force = false } = {}) {
            if (!axios) return state.products;
            const currentLang = state.languageCode || 'bg';

            if (!force && state.products && state.products.length > 0 && state.productsLanguage === currentLang) {
                return state.products;
            }

            if (fetchProductsPromise) {
                return fetchProductsPromise;
            }

            commit('setProductsLoading', true);
            fetchProductsPromise = axios.get(`/order/products/${currentLang}`)
                .then((response) => {
                    const products = response.data || [];
                    commit('setProducts', { products, langCode: currentLang });

                    // Preload images into browser cache so offers render immediately
                    if (typeof window !== 'undefined' && Array.isArray(products)) {
                        products.forEach((p) => {
                            if (p.imageSrc) {
                                const img = new Image();
                                img.src = p.imageSrc;
                            }
                        });
                    }

                    return products;
                })
                .catch((err) => {
                    console.error("Failed to preload products in store:", err);
                    return state.products || [];
                })
                .finally(() => {
                    commit('setProductsLoading', false);
                    fetchProductsPromise = null;
                });

            return fetchProductsPromise;
        }
    },
    getters: {
        isDev(state) {
            return state.env.toLowerCase() === "development";
        },
        isTest(state) {
            return state.env.toLowerCase() === "test";
        },
        isProduction(state) {
            return state.env.toLowerCase() === "production";
        },
        hasCookieConsent(state) {
            return state.cookieConsent;
        },
        cartItems(state) {
            return state.cart;
        },
        cartItemCount(state) {
            return state.cart.reduce((total, item) => total + (item.quantity || 0), 0);
        },
        cartTotalEuro(state) {
            const sum = state.cart.reduce((total, item) => total + ((item.quantity || 0) * (item.product.priceEuro || 0)), 0);
            return Math.round(sum * 100) / 100;
        },
        cartTotalLeva(state) {
            const sum = state.cart.reduce((total, item) => total + ((item.quantity || 0) * (item.product.priceLeva || 0)), 0);
            return Math.round(sum * 100) / 100;
        },
        freeDeliveryThreshold(state) {
            return parseFloat(state.terms?.freeDeliveryThreshold || 100);
        },
        calculatedDeliveryCost(state) {
            return state.calculatedDeliveryCost;
        }
    },
});
